import { create, type StoreApi, type UseBoundStore } from "zustand";
import type { CurrentUser, CurrentUserUpdate } from "../models/current-user";
import { mergeCurrentUserSnapshot } from "./apply-realtime-change";
import { type ApiClient, runStoreAction } from "./shared";

export type CurrentUserEntity = CurrentUser;

export interface CurrentUserStoreState {
	readonly error: string | null;
	readonly isLoading: boolean;
	reset: () => void;
	setValue: (value: CurrentUserEntity) => void;
	readonly value: CurrentUserEntity | null;
}

export type CurrentUserStore = UseBoundStore<StoreApi<CurrentUserStoreState>>;

/** PATCH /update/users returns the user row, not GET /get/currentuser. Keep workspace. */
const mergeCurrentUser = (previous: CurrentUserEntity | null, updated: CurrentUserEntity): CurrentUserEntity => {
	if (!previous || previous.id !== updated.id) {
		return updated;
	}
	return mergeCurrentUserSnapshot(previous, updated as unknown as Record<string, unknown>);
};

export const createCurrentUserStore = (apiClient: ApiClient) => {
	const useStore: CurrentUserStore = create<CurrentUserStoreState>((set) => ({
		value: null,
		isLoading: false,
		error: null,
		setValue: (value: CurrentUserEntity) => {
			set(() => ({ value, error: null }));
		},
		reset: () => {
			set(() => ({ value: null, isLoading: false, error: null }));
		},
	}));

	const get = async (): Promise<CurrentUserEntity> =>
		runStoreAction(
			(isLoading: boolean) => useStore.setState(() => ({ isLoading })),
			(error: string | null) => useStore.setState(() => ({ error })),
			async () => {
				const currentUser = await apiClient.get<CurrentUserEntity>("/get/currentuser");
				useStore.getState().setValue(currentUser);
				return currentUser;
			}
		);

	const getUser = async (userId: string): Promise<CurrentUserEntity> =>
		runStoreAction(
			(isLoading: boolean) => useStore.setState(() => ({ isLoading })),
			(error: string | null) => useStore.setState(() => ({ error })),
			async () => {
				const user = await apiClient.get<CurrentUserEntity>(`/get/users/${userId}`);
				return user;
			}
		);

	const update = async (currentUser: CurrentUserUpdate): Promise<CurrentUserEntity> =>
		runStoreAction(
			(isLoading: boolean) => useStore.setState(() => ({ isLoading })),
			(error: string | null) => useStore.setState(() => ({ error })),
			async () => {
				const previous = useStore.getState().value;
				const updated = await apiClient.post<CurrentUserEntity>(`/update/users/${currentUser.id}`, currentUser);
				const merged = mergeCurrentUser(previous, updated);
				useStore.getState().setValue(merged);
				return merged;
			}
		);

	const getFrillSsoToken = async (): Promise<string | null> => {
		const result = await apiClient.get<{ readonly ssoToken?: string }>("/integrations/frill/token");
		return typeof result.ssoToken === "string" && result.ssoToken.length > 0 ? result.ssoToken : null;
	};

	return {
		useStore,
		actions: {
			get,
			getFrillSsoToken,
			getUser,
			update,
		},
	};
};
